
import { useState } from "react";
import { TableCell, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Mail, Trash2 } from "lucide-react";
import { Invitation } from "@/services/employees/invitation/fetchInvitations";

interface InvitationTableRowProps {
  invitation: Invitation;
  storeName: string;
  onResendInvitation: (invitation: Invitation) => Promise<void>;
  onDeleteInvitation: (invitation: Invitation) => void;
  resendingIds: string[];
  deletingIds: string[];
}

export function InvitationTableRow({
  invitation,
  storeName,
  onResendInvitation,
  onDeleteInvitation,
  resendingIds,
  deletingIds
}: InvitationTableRowProps) {
  // Check if invitation is expired
  const isExpired = (expiresAt: string) => {
    return new Date(expiresAt) < new Date();
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };
  
  const expired = isExpired(invitation.expires_at);

  // For debugging - log the invitation data
  console.log("Rendering invitation row:", invitation);
  
  return (
    <TableRow key={invitation.id}>
      <TableCell>{`${invitation.first_name} ${invitation.last_name}`}</TableCell>
      <TableCell>{invitation.email}</TableCell>
      <TableCell>{storeName}</TableCell>
      <TableCell>
        <Badge variant="outline">
          {invitation.role.charAt(0).toUpperCase() + invitation.role.slice(1)}
        </Badge>
      </TableCell>
      <TableCell>{formatDate(invitation.created_at)}</TableCell>
      <TableCell>{formatDate(invitation.expires_at)}</TableCell>
      <TableCell>
        {expired ? (
          <Badge variant="destructive">Expired</Badge>
        ) : (
          <Badge variant="outline" className="bg-yellow-100 text-yellow-800 border-yellow-300">
            Pending
          </Badge>
        )}
      </TableCell>
      <TableCell className="text-right">
        <div className="flex justify-end gap-2">
          {expired && (
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => onResendInvitation(invitation)}
              disabled={resendingIds.includes(invitation.id)}
            >
              <Mail className="mr-2 h-4 w-4" />
              {resendingIds.includes(invitation.id) ? "Sending..." : "Re-invite"}
            </Button>
          )}
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => onDeleteInvitation(invitation)}
            disabled={deletingIds.includes(invitation.id)}
            className="text-red-500 hover:bg-red-50 hover:text-red-600"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
}
